import React, { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  XMarkIcon,
  PowerIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/api';

const ScenarioManagement = () => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [showScenarioModal, setShowScenarioModal] = useState(false);
  const [showCreateScenarioModal, setShowCreateScenarioModal] = useState(false);
  const [showEditScenarioModal, setShowEditScenarioModal] = useState(false);
  const [createScenarioForm, setCreateScenarioForm] = useState({
    scenarioId: '',
    description: '',
    taxRate: '',
    formula: '',
    saleType: '',
    requiredFields: [],
    isActive: true,
    taxRateText: '',
    sroScheduleNo: '',
    sroItemSerialNo: ''
  });
  const [editScenarioForm, setEditScenarioForm] = useState({
    scenarioId: '',
    description: '',
    taxRate: '',
    formula: '',
    saleType: '',
    requiredFields: [],
    isActive: true,
    taxRateText: '',
    sroScheduleNo: '',
    sroItemSerialNo: ''
  });
  const [saving, setSaving] = useState(false);

  // Available required fields options
  const availableFields = [
    'hsCode',
    'productDescription',
    'quantity',
    'unitPriceExcludingTax',
    'valueSalesExcludingST',
    'fixedNotifiedValueOrRetailPrice',
    'sroScheduleNo',
    'sroItemSerialNo'
  ];

  const loadScenarios = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { isActive: statusFilter === 'active' }),
        // Add cache busting parameter
        _t: Date.now()
      };

      console.log('🔄 Loading scenarios with params:', params);
      const response = await apiClient.get('/admin/scenarios', { params });
      
      console.log('📊 API Response:', {
        totalScenarios: response.data.data.scenarios.length,
        firstScenario: response.data.data.scenarios[0],
        hasSerialNo: response.data.data.scenarios.some(s => s.sroItemSerialNo)
      });
      
      setScenarios(response.data.data.scenarios);
      setTotalPages(response.data.data.pagination.pages);
      
      // Log scenarios with sroItemSerialNo for debugging
      const withSerialNo = response.data.data.scenarios.filter(s => s.sroItemSerialNo);
      if (withSerialNo.length > 0) {
        console.log('🔢 Scenarios with SRO Item Serial Numbers:', withSerialNo.map(s => ({
          id: s.scenarioId,
          sroScheduleNo: s.sroScheduleNo,
          sroItemSerialNo: s.sroItemSerialNo
        })));
      }
      
    } catch (error) {
      console.error('Failed to load scenarios:', error);
      if (error.response?.status === 404) {
        toast.error('Scenarios API endpoint not found. Please check if the backend is running.');
      } else if (error.response?.status === 500) {
        toast.error('Database error. Please check if the scenarios table exists.');
      } else {
        toast.error('Failed to load scenarios. Please try again.');
      }
      // Set empty data to prevent UI errors
      setScenarios([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter]);

  useEffect(() => {
    loadScenarios();
  }, [loadScenarios]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const viewScenarioDetails = async (scenarioId) => {
    try {
      const response = await apiClient.get(`/admin/scenarios/${scenarioId}`);
      setSelectedScenario(response.data.data);
      setShowScenarioModal(true);
    } catch (error) {
      console.error('Failed to load scenario details:', error);
      toast.error('Failed to load scenario details');
    }
  };

  const createScenario = async (e) => {
    e.preventDefault();

    if (!createScenarioForm.scenarioId || !createScenarioForm.description) {
      toast.error('Scenario ID and description are required');
      return;
    }

    try {
      setSaving(true);
      await apiClient.post('/admin/scenarios', createScenarioForm);
      toast.success('Scenario created successfully');
      setShowCreateScenarioModal(false);
      setCreateScenarioForm({
        scenarioId: '',
        description: '',
        taxRate: '',
        formula: '',
        saleType: '',
        requiredFields: [],
        isActive: true,
        taxRateText: '',
        sroScheduleNo: '',
        sroItemSerialNo: ''
      });
      loadScenarios();
    } catch (error) {
      console.error('Failed to create scenario:', error);
      toast.error(error.response?.data?.message || 'Failed to create scenario');
    } finally {
      setSaving(false);
    }
  };

  const updateScenario = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      await apiClient.put(`/admin/scenarios/${selectedScenario.id}`, editScenarioForm);
      toast.success('Scenario updated successfully');
      setShowEditScenarioModal(false);
      setSelectedScenario(null);
      loadScenarios();
    } catch (error) {
      console.error('Failed to update scenario:', error);
      toast.error(error.response?.data?.message || 'Failed to update scenario');
    } finally {
      setSaving(false);
    }
  };

  const deleteScenario = async (scenarioId, scenarioName) => {
    if (!window.confirm(`Are you sure you want to delete scenario "${scenarioName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiClient.delete(`/admin/scenarios/${scenarioId}`);
      toast.success('Scenario deleted successfully');
      loadScenarios();
      setShowScenarioModal(false);
    } catch (error) {
      console.error('Failed to delete scenario:', error);
      toast.error(error.response?.data?.message || 'Failed to delete scenario');
    }
  };

  const toggleScenarioStatus = async (scenarioId, currentStatus) => {
    try {
      await apiClient.patch(`/admin/scenarios/${scenarioId}/toggle-status`);
      toast.success(`Scenario ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      loadScenarios();
      if (selectedScenario && selectedScenario.id === scenarioId) {
        setSelectedScenario({ ...selectedScenario, isActive: !currentStatus });
      }
    } catch (error) {
      console.error('Failed to toggle scenario status:', error);
      toast.error('Failed to toggle scenario status');
    }
  };

  const openEditModal = (scenario) => {
    setSelectedScenario(scenario);
    setEditScenarioForm({
      scenarioId: scenario.scenarioId,
      description: scenario.description,
      taxRate: scenario.taxRate || '',
      formula: scenario.formula || '',
      saleType: scenario.saleType || '',
      requiredFields: scenario.requiredFields || [],
      isActive: scenario.isActive,
      taxRateText: scenario.taxRateText || '',
      sroScheduleNo: scenario.sroScheduleNo || '',
      sroItemSerialNo: scenario.sroItemSerialNo || ''
    });
    setShowEditScenarioModal(true);
  };

  const getStatusBadge = (isActive) => {
    return isActive
      ? 'bg-green-100 text-green-800'
      : 'bg-gray-100 text-gray-800';
  };

  const handleRequiredFieldToggle = (field, isEdit = false) => {
    const form = isEdit ? editScenarioForm : createScenarioForm;
    const setForm = isEdit ? setEditScenarioForm : setCreateScenarioForm;
    
    const currentFields = form.requiredFields || [];
    const updatedFields = currentFields.includes(field)
      ? currentFields.filter(f => f !== field)
      : [...currentFields, field];
    
    setForm({ ...form, requiredFields: updatedFields });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Scenario Management</h2>
          <p className="text-sm text-gray-600">Manage FBR tax scenarios and their configurations</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => {
              console.log('🔄 Force refreshing scenarios...');
              loadScenarios();
            }}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
            Refresh Data
          </button>
          <button
            onClick={() => setShowCreateScenarioModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Scenario
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search scenarios by ID, description, or sale type..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={handleStatusFilter}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Scenarios Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Scenario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tax Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sale Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-500">Loading scenarios...</span>
                    </div>
                  </td>
                </tr>
              ) : scenarios.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No scenarios found
                  </td>
                </tr>
              ) : (
                scenarios.map((scenario) => (
                  <tr key={scenario.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {scenario.scenarioId}
                        </div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {scenario.description}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {scenario.taxRateText || scenario.taxRate || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {scenario.saleType || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(scenario.isActive)}`}>
                        {scenario.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(scenario.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => viewScenarioDetails(scenario.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(scenario)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Edit Scenario"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => toggleScenarioStatus(scenario.id, scenario.isActive)}
                          className={`${scenario.isActive ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}`}
                          title={scenario.isActive ? 'Deactivate' : 'Activate'}
                        >
                          <PowerIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteScenario(scenario.id, scenario.scenarioId)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Scenario"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View Scenario Modal */}
      {showScenarioModal && selectedScenario && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Scenario Details: {selectedScenario.scenarioId}
              </h3>
              <button
                onClick={() => setShowScenarioModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <p className="mt-1 text-sm text-gray-900">{selectedScenario.description}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tax Rate</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedScenario.taxRateText || selectedScenario.taxRate || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(selectedScenario.isActive)}`}>
                    {selectedScenario.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Sale Type</label>
                <p className="mt-1 text-sm text-gray-900">{selectedScenario.saleType || 'N/A'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Formula</label>
                <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded">
                  {selectedScenario.formula || 'N/A'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">SRO Schedule Number</label>
                <p className="mt-1 text-sm text-gray-900">{selectedScenario.sroScheduleNo || 'N/A'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">SRO Item Serial Number</label>
                <p className="mt-1 text-sm text-gray-900">{selectedScenario.sroItemSerialNo || 'N/A'}</p>
              </div>

              {selectedScenario.requiredFields && selectedScenario.requiredFields.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Required Fields</label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {selectedScenario.requiredFields.map((field) => (
                      <span
                        key={field}
                        className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                      >
                        {field}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500">
                <div>
                  <label className="block font-medium">Created</label>
                  <p>{new Date(selectedScenario.createdAt).toLocaleString()}</p>
                </div>
                <div>
                  <label className="block font-medium">Last Updated</label>
                  <p>{new Date(selectedScenario.updatedAt).toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => openEditModal(selectedScenario)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Edit Scenario
              </button>
              <button
                onClick={() => setShowScenarioModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Scenario Modal */}
      {showCreateScenarioModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Create New Scenario</h3>
              <button
                onClick={() => setShowCreateScenarioModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={createScenario} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Scenario ID *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., SN029"
                    value={createScenarioForm.scenarioId}
                    onChange={(e) => setCreateScenarioForm({ ...createScenarioForm, scenarioId: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Tax Rate Text
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 18%, Rs. 50/unit"
                    value={createScenarioForm.taxRateText}
                    onChange={(e) => setCreateScenarioForm({ ...createScenarioForm, taxRateText: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description *
                </label>
                <textarea
                  rows={3}
                  placeholder="Detailed description of the scenario..."
                  value={createScenarioForm.description}
                  onChange={(e) => setCreateScenarioForm({ ...createScenarioForm, description: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Tax Rate (Numeric)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 18, 0.18"
                    value={createScenarioForm.taxRate}
                    onChange={(e) => setCreateScenarioForm({ ...createScenarioForm, taxRate: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Tax Rate Text
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 18%, Rs. 3 per unit"
                    value={createScenarioForm.taxRateText}
                    onChange={(e) => setCreateScenarioForm({ ...createScenarioForm, taxRateText: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    SRO Schedule Number
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., EIGHTH SCHEDULE"
                    value={createScenarioForm.sroScheduleNo}
                    onChange={(e) => setCreateScenarioForm({ ...createScenarioForm, sroScheduleNo: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    SRO Item Serial Number
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 1, 2, 3"
                    value={createScenarioForm.sroItemSerialNo}
                    onChange={(e) => setCreateScenarioForm({ ...createScenarioForm, sroItemSerialNo: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Sale Type
                </label>
                <input
                  type="text"
                  placeholder="e.g., Standard goods, Services, etc."
                  value={createScenarioForm.saleType}
                  onChange={(e) => setCreateScenarioForm({ ...createScenarioForm, saleType: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Formula
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g., valueSalesExcludingST * 0.18"
                  value={createScenarioForm.formula}
                  onChange={(e) => setCreateScenarioForm({ ...createScenarioForm, formula: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Required Fields
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {availableFields.map((field) => (
                    <label key={field} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={createScenarioForm.requiredFields.includes(field)}
                        onChange={() => handleRequiredFieldToggle(field)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{field}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={createScenarioForm.isActive}
                    onChange={(e) => setCreateScenarioForm({ ...createScenarioForm, isActive: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateScenarioModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Creating...' : 'Create Scenario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Scenario Modal */}
      {showEditScenarioModal && selectedScenario && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Edit Scenario: {selectedScenario.scenarioId}
              </h3>
              <button
                onClick={() => setShowEditScenarioModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={updateScenario} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Scenario ID
                  </label>
                  <input
                    type="text"
                    value={editScenarioForm.scenarioId}
                    onChange={(e) => setEditScenarioForm({ ...editScenarioForm, scenarioId: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Tax Rate Text
                  </label>
                  <input
                    type="text"
                    value={editScenarioForm.taxRateText}
                    onChange={(e) => setEditScenarioForm({ ...editScenarioForm, taxRateText: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={editScenarioForm.description}
                  onChange={(e) => setEditScenarioForm({ ...editScenarioForm, description: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Tax Rate (Numeric)
                  </label>
                  <input
                    type="text"
                    value={editScenarioForm.taxRate}
                    onChange={(e) => setEditScenarioForm({ ...editScenarioForm, taxRate: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    SRO Schedule Number
                  </label>
                  <input
                    type="text"
                    value={editScenarioForm.sroScheduleNo}
                    onChange={(e) => setEditScenarioForm({ ...editScenarioForm, sroScheduleNo: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    SRO Item Serial Number
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 1, 2, 3"
                    value={editScenarioForm.sroItemSerialNo}
                    onChange={(e) => setEditScenarioForm({ ...editScenarioForm, sroItemSerialNo: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  {/* Empty div for grid alignment */}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Sale Type
                </label>
                <input
                  type="text"
                  value={editScenarioForm.saleType}
                  onChange={(e) => setEditScenarioForm({ ...editScenarioForm, saleType: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Formula
                </label>
                <textarea
                  rows={2}
                  value={editScenarioForm.formula}
                  onChange={(e) => setEditScenarioForm({ ...editScenarioForm, formula: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Required Fields
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {availableFields.map((field) => (
                    <label key={field} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editScenarioForm.requiredFields.includes(field)}
                        onChange={() => handleRequiredFieldToggle(field, true)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{field}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editScenarioForm.isActive}
                    onChange={(e) => setEditScenarioForm({ ...editScenarioForm, isActive: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditScenarioModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Updating...' : 'Update Scenario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScenarioManagement;
